import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import mapboxgl from 'mapbox-gl';
import {
  IActivo,
  IEmergencia,
  ILayerMapaPersonalizado,
  IPopulate,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
  IFilter,
  IUbicacion,
  IUbicacionBase,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { LoginService } from '../../../../login/login.service';
import { TipoMapaBase } from '../../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { MapaFacilV2Service } from '../../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { MapaFacilV2Component } from '../../../../../auxiliares/mapbox/mapa-facil-v2.component';
import { EmergenciasMedicasLayerManager } from '../../../../../auxiliares/mapbox/entity-managers/emergencias-medicas-layer.manager';
import {
  IPorcionTortaEmergencias,
  PieChartEmergenciasComponent,
} from './pie-chart-emergencias/pie-chart-emergencias.component';
import {
  ESTADOS_EMERGENCIA,
  ESTADOS_FINALES,
  PRIORIDADES,
  estadoDe,
  presentacionEstado,
  presentacionPrioridad,
} from '../../estado-emergencia';
import {
  CapasEmergencias,
  FlotanteEmergenciasMedicasComponent,
  ModoIconoEmergencia,
  prioridadMapa,
} from './flotante-emergencias-medicas/flotante-emergencias-medicas.component';

interface IAmbulanciaMapa extends IActivo {
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
  asignada?: boolean;
}


@Component({
  selector: 'app-mapa-emergencia-medica',
  templateUrl: './mapa-emergencias-medicas.component.html',
  styleUrl: './mapa-emergencias-medicas.component.scss',
  imports: [
    
    MatIconModule,
    
    MapaFacilV2Component,
    PieChartEmergenciasComponent,
    FlotanteEmergenciasMedicasComponent,
  ],
})
export class MapaEmergenciasMedicasComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private layerManager = inject(EmergenciasMedicasLayerManager);

  // Datos
  public emergencias: IEmergencia[] = [];
  public emergenciasFiltradas: IEmergencia[] = [];
  public hospitales: IUbicacionBase<'Hospital'>[] = [];
  public bases: IUbicacionBase<'Centro de Atención'>[] = [];
  public ambulancias: IAmbulanciaMapa[] = [];
  private vehiculos: IActivo[] = [];
  private reportes: IReporteBase<'Tracker 4G'>[] = [];

  private emergencias$?: Subscription;
  private hospitales$?: Subscription;
  private bases$?: Subscription;
  private vehiculos$?: Subscription;
  private reportes$?: Subscription;
  private idsVehiculosReportes = '';

  // Estado del flotante
  private capas: CapasEmergencias = {
    emergencias: true,
    ambulancias: true,
    hospitales: true,
    bases: true,
  };
  public modo: ModoIconoEmergencia = 'prioridad';

  // Dona del resumen (va dentro del panel, junto a Código o Estados)
  public datosTorta: IPorcionTortaEmergencias[] = [];

  // Entidades que el mapa re-aplica al cambiar la capa base
  public allowedTypes: string[] = [
    'emergenciasMedicas',
    'hospitales',
    'ambulancias',
    'centrosAtencion',
  ];

  // Capa base
  public readonly TIPO_MAPA_BASE_KEY =
    'tipoMapaBaseEmergenciasMedicasComponent';
  public tipoMapaBase: TipoMapaBase =
    this.leer(this.TIPO_MAPA_BASE_KEY) || 'osm';
  public mapasPersonalizados: ILayerMapaPersonalizado[] =
    LoginService.getCliente()?.mapLayers ?? [];

  public map: mapboxgl.Map | null = null;

  // ---------------------------------------------------------------------------
  // Listados

  private listarEmergencias(): void {
    // Mismo criterio que el tablero y los listados de activas
    const filter: Record<string, unknown> = {
      tipo: 'Médica',
      'ultimoEventoEmergencia.estado': { $nin: ESTADOS_FINALES },
      importada: { $ne: true },
    };
    const populate: IPopulate[] = [{ path: 'destinatarioAsistencia' }];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      sort: '-fechaCreacion',
      unlimited: true,
      includeChildren: true,
    };
    this.emergencias$?.unsubscribe();
    this.emergencias$ = this.lista2.emergencias(query, 30).subscribe({
      next: (data) => {
        if (!HelperService.cambiaronLosDatos(this.emergencias, data.datos)) {
          return;
        }
        this.emergencias = data.datos;
        // El flotante refiltra (ngOnChanges) y emite emergenciasFiltradas
        this.marcarAmbulanciasAsignadas();
        this.dibujarAmbulancias();
      },
      error: () => this.helper.notifError('Error al listar emergencias'),
    });
  }

  private listarHospitales(): void {
    const query: IQueryParam = {
      populate: JSON.stringify([{ path: 'cliente', select: 'nombre' }]),
      unlimited: true,
      includeChildren: true,
    };
    this.hospitales$?.unsubscribe();
    this.hospitales$ = this.lista2.hospitals(query, 30).subscribe({
      next: (data) => {
        this.hospitales = data.datos as IUbicacionBase<'Hospital'>[];
        this.dibujarHospitales();
      },
      error: () => this.helper.notifError('Error al listar hospitales'),
    });
  }

  private listarBases(): void {
    const filter: IFilter<IUbicacion> = {
      'valores.tipoEmergencia': 'Médica',
    } as IFilter<IUbicacion>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify([{ path: 'cliente', select: 'nombre' }]),
      unlimited: true,
      includeChildren: true,
    };
    this.bases$?.unsubscribe();
    this.bases$ = this.lista2.centroAtencions(query, 30).subscribe({
      next: (data) => {
        this.bases = (
          data.datos as IUbicacionBase<'Centro de Atención'>[]
        ).filter((c) => c?.valores?.tipoEmergencia === 'Médica');
        this.dibujarBases();
      },
      error: () =>
        this.helper.notifError('Error al listar bases de operaciones'),
    });
  }

  private listarAmbulancias(): void {
    const populate: IPopulate[] = [
      { path: 'cliente', select: 'nombre' },
      { path: 'tracker', select: 'nombre tipo frecReporte' },
      { path: 'grupo', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ funcion: 'Ambulancia' }),
      populate: JSON.stringify(populate),
      sort: 'identificacion',
      unlimited: true,
      includeChildren: true,
    };
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2.vehiculos(query, 30).subscribe({
      next: (data) => {
        this.vehiculos = data.datos;
        this.suscribirseAReportes();
        this.unirReportes();
      },
      error: () => this.helper.notifError('Error al listar ambulancias'),
    });
  }

  /** Re-suscribe sólo si cambió el conjunto de ambulancias. */
  private suscribirseAReportes(): void {
    const ids = this.vehiculos
      .map((v) => v._id)
      .filter((id): id is string => !!id)
      .sort();
    const clave = ids.join(',');
    if (clave === this.idsVehiculosReportes) return;
    this.idsVehiculosReportes = clave;

    this.reportes$?.unsubscribe();
    if (!ids.length) {
      this.reportes = [];
      return;
    }
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: { $in: ids },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    this.reportes$ = this.lista2
      .ultimosReportesGenericos<'Tracker 4G'>(query)
      .subscribe((data) => {
        this.reportes = data.datos;
        this.unirReportes();
      });
  }

  private unirReportes(): void {
    this.ambulancias = this.vehiculos
      .map((v) => ({
        ...v,
        ultimoReporte: this.reportes.find((r) =>
          r.idsAsignados?.includes(v._id!),
        ),
      }))
      .filter((a) => !!a.ultimoReporte?.valores?.geojson?.coordinates);
    this.marcarAmbulanciasAsignadas();
    this.dibujarAmbulancias();
  }

  private marcarAmbulanciasAsignadas(): void {
    const asignadas = new Set(
      this.emergencias
        .map(
          (e) => e.ultimoEventoEmergencia?.detallesEmergencias?.idMovilAsignado,
        )
        .filter(Boolean),
    );
    for (const a of this.ambulancias) {
      a.asignada = asignadas.has(a._id);
    }
  }

  // ---------------------------------------------------------------------------
  // Dibujo

  private dibujarEmergencias(): void {
    if (!this.mapaFacilV2Service.isReady()) return;
    this.mapaFacilV2Service.updateData({
      emergenciasMedicas: this.capas.emergencias
        ? this.emergenciasFiltradas.filter(
            (e) => !!e.ubicacionDestino?.coordinates,
          )
        : [],
    });
  }

  private dibujarHospitales(): void {
    if (!this.mapaFacilV2Service.isReady()) return;
    this.mapaFacilV2Service.updateData({
      hospitales: this.capas.hospitales
        ? this.hospitales.filter((h) => !!h.geojson)
        : [],
    });
  }

  private dibujarBases(): void {
    if (!this.mapaFacilV2Service.isReady()) return;
    this.mapaFacilV2Service.updateData({
      centrosAtencion: this.capas.bases
        ? this.bases.filter((c) => !!c.geojson?.coordinates)
        : [],
    });
  }

  private dibujarAmbulancias(): void {
    if (!this.mapaFacilV2Service.isReady()) return;
    this.mapaFacilV2Service.updateData({
      ambulancias: this.capas.ambulancias ? this.ambulancias : [],
    });
  }

  private dibujarTodo(): void {
    this.dibujarEmergencias();
    this.dibujarHospitales();
    this.dibujarBases();
    this.dibujarAmbulancias();
  }

  // ---------------------------------------------------------------------------
  // Eventos del flotante

  public onEmergenciasFiltradas(lista: IEmergencia[]): void {
    this.emergenciasFiltradas = lista;
    this.actualizarTorta();
    this.dibujarEmergencias();
  }

  public onCapasChange(capas: CapasEmergencias): void {
    this.capas = capas;
    this.dibujarTodo();
  }

  public onModoChange(modo: ModoIconoEmergencia): void {
    this.modo = modo;
    this.layerManager.setModo(modo);
    this.actualizarTorta();
    this.dibujarEmergencias();
  }

  private actualizarTorta(): void {
    const conteo = new Map<string, number>();
    const clave =
      this.modo === 'prioridad'
        ? (e: IEmergencia) => prioridadMapa(e)
        : (e: IEmergencia) => estadoDe(e) || 'Pendiente';
    for (const e of this.emergenciasFiltradas) {
      const k = clave(e);
      conteo.set(k, (conteo.get(k) ?? 0) + 1);
    }
    const orden: string[] =
      this.modo === 'prioridad' ? [...PRIORIDADES, ''] : ESTADOS_EMERGENCIA;
    const presentar =
      this.modo === 'prioridad' ? presentacionPrioridad : presentacionEstado;
    this.datosTorta = orden
      .filter((k) => conteo.get(k))
      .map((k) => ({
        name: presentar(k).label,
        y: conteo.get(k)!,
        color: presentar(k).color,
      }));
  }

  private leer(clave: string): string | null {
    try {
      return localStorage.getItem(clave);
    } catch {
      return null;
    }
  }

  public onMapReady(map: mapboxgl.Map): void {
    this.map = map;
    this.layerManager.setModo(this.modo);
    this.dibujarTodo();
  }

  ngOnInit(): void {
    this.listarEmergencias();
    this.listarHospitales();
    this.listarBases();
    this.listarAmbulancias();
  }

  ngOnDestroy(): void {
    this.emergencias$?.unsubscribe();
    this.hospitales$?.unsubscribe();
    this.bases$?.unsubscribe();
    this.vehiculos$?.unsubscribe();
    this.reportes$?.unsubscribe();
    // El servicio es singleton: limpiar para no arrastrar datos a otro mapa
    this.mapaFacilV2Service.clearAllData();
  }
}
